import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Paper,
  Divider,
  Chip
} from '@mui/material';
import {
  NotificationsActive,
  CheckCircle,
  Cancel,
  Person,
  Schedule,
  LocalCafe
} from '@mui/icons-material';
import { useOrders } from '../context/OrdersContext';
import ConfirmAvailabilityModal from './ConfirmAvailabilityModal';
import UnavailableItemsModal from './UnavailableItemsModal';
import { formatMoscowTime } from '../utils/timeUtils';
import { orderDisplayNumber } from '../utils/orderDisplay';

const NewOrderModal = ({ order, open, onClose }) => {
  const [timeLeft, setTimeLeft] = useState(180); // 180 секунд = 3 минуты
  const { confirmOrder, rejectOrder, returnToAwaitingConfirmation, newOrdersQueue } = useOrders();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAvailabilityCheck, setShowAvailabilityCheck] = useState(false);
  const [showUnavailableItemsModal, setShowUnavailableItemsModal] = useState(false);

  // Сбрасываем все состояния при смене заказа
  useEffect(() => {
    if (order) {
      console.log(`[NewOrderModal] Новый заказ #${order.id}, сброс состояний`);
      setIsProcessing(false);
      setShowAvailabilityCheck(false);
      setShowUnavailableItemsModal(false);
      setTimeLeft(180);
    }
  }, [order?.id]);

  useEffect(() => {
    if (open && order) {

      // Функция автоматической отмены заказа при истечении времени
      const handleAutoReject = async () => {
        console.log(`[NewOrderModal] Автоматическая отмена заказа #${order.id} - время истекло`);
        setIsProcessing(true);
        
        try {
          await rejectOrder(order.id);
          console.log(`[NewOrderModal] Заказ #${order.id} автоматически отменен`);
          setIsProcessing(false);
          
          // После отклонения заказ автоматически удаляется из очереди
          // Модальное окно автоматически покажет следующий заказ или закроется если очередь пуста
          console.log('[NewOrderModal] Переход к следующему заказу в очереди');
        } catch (error) {
          console.error('Ошибка автоматической отмены заказа:', error);
          setIsProcessing(false);
        }
      };

      const timer = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timer);
            // Автоматически отменить заказ при истечении времени
            handleAutoReject();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [open, order, rejectOrder]);

  const handleConfirm = () => {
    if (!order || isProcessing) return;
    // Открываем модальное окно проверки наличия
    setShowAvailabilityCheck(true);
  };

  const handleFinalConfirm = async () => {
    if (!order) return;
    
    setIsProcessing(true);
    try {
      await confirmOrder(order.id);
      setShowAvailabilityCheck(false);
      setIsProcessing(false);
      
      // После подтверждения заказ автоматически удаляется из очереди
      // Модальное окно автоматически покажет следующий заказ или закроется если очередь пуста
      console.log('[NewOrderModal] Заказ подтвержден, переход к следующему в очереди');
    } catch (error) {
      console.error('Ошибка подтверждения заказа:', error);
      setIsProcessing(false);
    }
  };

  const handleNotAvailable = () => {
    if (!order) return;
    // Открываем модальное окно для указания недостающих позиций
    setShowUnavailableItemsModal(true);
  };

  const handleSubmitUnavailableItems = async (unavailableItemsDescription) => {
    if (!order) return;
    
    setIsProcessing(true);
    try {
      await returnToAwaitingConfirmation(order.id, unavailableItemsDescription);
      console.log(`[NewOrderModal] Заказ #${order.id} возвращен с комментарием о недостающих позициях`);
      setShowUnavailableItemsModal(false);
      setShowAvailabilityCheck(false);
      setIsProcessing(false);
      
      // После возврата заказ автоматически удаляется из очереди
      // Модальное окно автоматически покажет следующий заказ или закроется если очередь пуста
      console.log('[NewOrderModal] Заказ возвращен, переход к следующему в очереди');
    } catch (error) {
      console.error('Ошибка возврата заказа:', error);
      setIsProcessing(false);
      throw error;
    }
  };

  const handleCancelUnavailableItems = () => {
    // Закрываем модальное окно и возвращаемся к проверке наличия
    setShowUnavailableItemsModal(false);
  };

  const handleCancelFromAvailabilityCheck = async () => {
    if (!order) return;
    
    setIsProcessing(true);
    try {
      await rejectOrder(order.id);
      setShowAvailabilityCheck(false);
      setIsProcessing(false);
      
      // После отклонения заказ автоматически удаляется из очереди
      // Модальное окно автоматически покажет следующий заказ или закроется если очередь пуста
      console.log('[NewOrderModal] Заказ отклонен, переход к следующему в очереди');
    } catch (error) {
      console.error('Ошибка отмены заказа:', error);
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!order || isProcessing) return;
    
    setIsProcessing(true);
    try {
      await rejectOrder(order.id);
      setIsProcessing(false);
      
      // После отклонения заказ автоматически удаляется из очереди
      // Модальное окно автоматически покажет следующий заказ или закроется если очередь пуста
      console.log('[NewOrderModal] Заказ отклонен, переход к следующему в очереди');
    } catch (error) {
      console.error('Ошибка отклонения заказа:', error);
      setIsProcessing(false);
    }
  };

  // Используем утилиту для форматирования московского времени
  const formatTime = formatMoscowTime;

  const progress = (timeLeft / 60) * 100;

  if (!order) return null;

  return (
    <>
      <Dialog
        open={open && !showAvailabilityCheck}
        onClose={isProcessing ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: '400px'
          }
        }}
      >
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          p: 3,
          textAlign: 'center',
          position: 'relative'
        }}
      >
        {newOrdersQueue.length > 1 && (
          <Chip
            label={`Еще ${newOrdersQueue.length - 1} в очереди`}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
              fontWeight: 'bold',
              fontSize: '0.875rem'
            }}
          />
        )}
        <NotificationsActive sx={{ fontSize: 60, mb: 1 }} />
        <Typography variant="h4" component="h2" gutterBottom>
          Новый заказ!
        </Typography>
        <Typography variant="subtitle1">
          Пожалуйста, подтвердите заказ
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            bgcolor: timeLeft <= 10 ? 'error.light' : 'grey.100',
            transition: 'background-color 0.3s'
          }}
        >
          <Typography
            variant="h6"
            align="center"
            sx={{
              color: timeLeft <= 10 ? 'error.dark' : 'text.primary',
              fontWeight: 'bold'
            }}
          >
            Осталось: {timeLeft} сек
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mt: 1,
              height: 8,
              borderRadius: 4,
              bgcolor: 'grey.300',
              '& .MuiLinearProgress-bar': {
                bgcolor: timeLeft <= 10 ? 'error.main' : 'success.main'
              }
            }}
          />
        </Paper>

        <Box mb={2}>
          <Box display="flex" alignItems="center" mb={1.5}>
            <Person sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Заказ #{orderDisplayNumber(order)}
            </Typography>
          </Box>

          {order.estimated_ready_time && (
            <Box display="flex" alignItems="center" mb={2}>
              <Schedule sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1" color="text.secondary">
                Готовность к: <strong>{formatTime(order.estimated_ready_time)}</strong>
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Box display="flex" alignItems="center" mb={1}>
            <LocalCafe sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Состав заказа:
            </Typography>
          </Box>

          <List dense>
            {order.items && order.items.map((item, index) => {
              // Данные уже трансформированы в OrdersContext
              const itemName = item.item_name || 'Позиция';
              const sizeName = item.size_name || '';
              const hasAddons = item.addons && item.addons.length > 0;

              return (
                <Box key={item.id || index} sx={{ mb: 2 }}>
                  {/* Основная позиция */}
                  <ListItem disableGutters>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body1" fontWeight="medium">
                            {itemName}{sizeName ? ` (${sizeName})` : ''} × {item.quantity || 1}
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {item.price_at_time || 0} ₽
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  
                  {/* Добавки */}
                  {hasAddons && (
                    <Box sx={{ ml: 2, mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Дополнительно:
                      </Typography>
                      {item.addons.map((addon, addonIndex) => (
                        <Box 
                          key={addonIndex} 
                          display="flex" 
                          justifyContent="space-between" 
                          alignItems="center"
                          sx={{ ml: 1, mb: 0.5 }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            + {addon.addon_name || addon.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            +{addon.price_at_time || 0} ₽
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </List>

          {/* Скидка (промокод) */}
          {order.discount_amount != null && Number(order.discount_amount) > 0 && (
            <Box mt={2} pt={2} borderTop={1} borderColor="divider">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1" color="text.secondary">
                  Скидка (промокод)
                </Typography>
                <Typography variant="body1" sx={{ color: 'success.main' }}>
                  −{Number(order.discount_amount).toFixed(2)} ₽
                </Typography>
              </Box>
            </Box>
          )}

          {/* Сервисный сбор */}
          {order.service_commission && order.service_commission > 0 && (
            <Box mt={2} pt={2} borderTop={1} borderColor="divider">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1" color="text.secondary">
                  Сервисный сбор
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {order.service_commission} ₽
                </Typography>
              </Box>
            </Box>
          )}

          {/* Итого */}
          {order.total_amount && (
            <Box mt={order.service_commission || (order.discount_amount > 0) ? 1 : 2} pt={order.service_commission || (order.discount_amount > 0) ? 1 : 2} borderTop={order.service_commission || (order.discount_amount > 0) ? 0 : 1} borderColor="divider">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                  Итого:
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {order.total_amount} ₽
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={handleReject}
          variant="outlined"
          color="error"
          size="large"
          fullWidth
          startIcon={<Cancel />}
          disabled={isProcessing}
        >
          Отклонить
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="success"
          size="large"
          fullWidth
          startIcon={<CheckCircle />}
          disabled={isProcessing}
          autoFocus
        >
          {isProcessing ? 'Подтверждение...' : 'Подтвердить'}
        </Button>
      </DialogActions>
      </Dialog>

      <ConfirmAvailabilityModal
        order={order}
        open={showAvailabilityCheck && !showUnavailableItemsModal}
        onConfirm={handleFinalConfirm}
        onNotAvailable={handleNotAvailable}
        onCancel={handleCancelFromAvailabilityCheck}
      />

      <UnavailableItemsModal
        order={order}
        open={showUnavailableItemsModal}
        onSubmit={handleSubmitUnavailableItems}
        onCancel={handleCancelUnavailableItems}
      />
    </>
  );
};

export default NewOrderModal;

